import type { Command } from 'commander';
import chalk from 'chalk';
import { getSDKClient } from '../../lib/sdk-client.js';
import { OutputFormatter, getJsonFlag } from '../../lib/output.js';
import { MutagentError, ApiError, handleError } from '../../lib/errors.js';
import {
  providerSettingsLink,
  renderPlainCard,
  echoDirectiveToStderr,
} from '../../lib/ui-links.js';

interface DeleteOptions {
  force?: boolean;
}

export function registerDeleteCommand(parent: Command): void {
  parent
    .command('delete')
    .description('Delete a provider configuration')
    .argument('<id>', 'Provider ID (from: mutagent providers list)')
    .option('-f, --force', 'Skip confirmation prompt')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent providers delete <id>
  ${chalk.dim('$')} mutagent providers delete <id> --force
  ${chalk.dim('$')} mutagent providers delete <id> --force --json

${chalk.dim('Note: --force is required. The CLI is non-interactive — confirm with the user via your native flow, then pass --force.')}
${chalk.dim('Warning: API keys are AES-256-GCM encrypted and irrecoverable after deletion. Agents referencing this provider will lose their model config.')}
    `)
    .action(async (id: string, options: DeleteOptions) => {
      const isJson = getJsonFlag(parent);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      try {
        // In --json mode, auto-skip confirmation (agent controls flow).
        // Otherwise require --force.
        if (!options.force && !isJson) {
          throw new MutagentError(
            'CONFIRMATION_REQUIRED',
            `Deleting provider ${id} requires confirmation. ` +
            'Warning: encrypted API keys are irrecoverable after deletion.',
            'Run: mutagent providers delete --help\n' +
            `[Agent: Ask the user to confirm via MultiChoice, then pass --force]\n` +
            `Use --force to confirm: mutagent providers delete ${id} --force`
          );
        }

        const client = await getSDKClient();

        try {
          await client.deleteProvider(id);
        } catch (error) {
          // 404 = already deleted = idempotent success
          if (error instanceof ApiError && error.statusCode === 404) {
            if (isJson) {
              const directive = buildProviderDeletedDirective(id);
              output.output({
                success: true,
                deletedId: id,
                _links: { providers: providerSettingsLink() },
                _directive: directive,
              });
              echoDirectiveToStderr(directive);
            } else {
              output.success(`Provider ${id} already deleted (idempotent)`);
            }
            return;
          }
          throw error;
        }

        if (isJson) {
          const directive = buildProviderDeletedDirective(id);
          echoDirectiveToStderr(directive);
          output.output({
            success: true,
            deletedId: id,
            _links: { providers: providerSettingsLink() },
            _directive: directive,
          });
        } else {
          output.success(`Deleted provider: ${id}`);
          console.log(`  Settings: ${providerSettingsLink()}`);
        }
      } catch (error) {
        handleError(error, isJson);
      }
    });
}

function buildProviderDeletedDirective(id: string) {
  const title = `Provider Deleted — ${id}`;
  const settingsUrl = providerSettingsLink();
  const rows = [{ label: 'Provider ID', value: id }];
  const links = [{ label: 'Settings', url: settingsUrl }];
  const next = [`mutagent providers list --json`];
  return {
    display: 'status_card' as const,
    template: 'provider_deleted',
    title,
    fields: { providerId: id },
    links: { settings: settingsUrl },
    next,
    instruction: 'MANDATORY: Display this card to the user before proceeding.',
    renderedCard: renderPlainCard({ title, icon: 'x', rows, links, next }),
  };
}
